import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { toast } from 'Components/DesignSystem/Toast/useToast';
import { AppContext } from 'Context/AppContext';
import { CopyIconBlue, XIcon } from '../../../../assests/GmsIcons';
import { Delete } from 'Components/DesignSystem/Iconography/Icons';
import SidePanel from 'Components/DesignSystem/SidePanel/SidePanel';
import Accordion from 'Components/DesignSystem/Accordion/Accordion';
import Button from 'Components/DesignSystem/Button/Button';
import TextField from 'Components/DesignSystem/TextField/TextField';
import Dropdown from 'Components/DesignSystem/Dropdown/Dropdown';
import Dialog from 'Components/DesignSystem/Dialog/Dialog';
import Spinner from 'Components/DesignSystem/Spinner/Spinner';
import Alert from 'Components/DesignSystem/Alert/Alert';
import EmptyState from 'Components/DesignSystem/EmptyState/EmptyState';
import NoDataIllustration from 'Components/DesignSystem/EmptyState/NoDataIllustration';
import ErrorState from 'Components/DesignSystem/ErrorState/ErrorState';
import InfoTooltip from 'Components/DesignSystem/Tooltip/InfoTooltip';
import Tag from 'Components/DesignSystem/Tag/Tag';
import Typography from 'Components/DesignSystem/Typography/Typography';
import {
    createWidget,
    deleteWidget,
    getPromotedModelsForWidget,
    listWidgets,
    markWidgetsViewed,
    updateWidget,
} from '../Pages/DefineModel/ModelServices/ModalServices';
import EmbedCodeDialog from './EmbedCodeDialog';

type TWidget = {
    id: string;
    title: string;
    model_id: string;
    model_name?: string;
    channel: string;
    industry: string;
    created_at?: string;
    updated_at?: string;
    /** ADO #14217 (US-4): unviewed until the Embed code dialog is next opened. */
    viewed?: boolean;
};

type TPromotedModel = {
    id: string;
    name: string;
};

type Props = {
    industry: string;
    channel: string;
    onClose: () => void;
    onProjectListRefresh?: () => void;
};

/**
 * The Guide Widgets drawer on the design system (§ 91): DS SidePanel shell
 * (md = 416px, Escape + backdrop dismissal, focus trap — the legacy panel had
 * none of those), one controlled DS Accordion per widget (§ 90 controlled
 * `open` gives the exclusive single-open and the expand-after-create flows),
 * DS TextField/Dropdown(searchable, replacing both ReactSelects)/Buttons in
 * the bodies, and DS Dialogs for the create / change-model / delete-confirm
 * modals. All fetch/save/permission logic is unchanged.
 *
 * The truncation tooltip on widget titles keeps the legacy scrollWidth check
 * on purpose: it must appear only when the title actually overflows, which a
 * declarative always-on-hover Tooltip cannot express.
 */
const GuideWidgetPanel: React.FC<Props> = ({ industry, channel, onClose, onProjectListRefresh }) => {
    const appData = useContext(AppContext);
    const canCreateWidgets = appData?.pm?.checkPermission('gmsguide_widgetcreate') ?? false;
    const canUpdateWidgets = appData?.pm?.checkPermission('gmsguide_widgetupdate') ?? false;
    const canDeleteWidgets = appData?.pm?.checkPermission('gmsguide_widgetdelete') ?? false;

    const [widgets, setWidgets] = useState<TWidget[]>([]);
    const [loading, setLoading] = useState(true);
    const [expandedId, setExpandedId] = useState<string | null>(null);

    // Create modal state
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [createTitle, setCreateTitle] = useState('');
    const [createModelId, setCreateModelId] = useState('');
    const [createModels, setCreateModels] = useState<TPromotedModel[]>([]);
    const [creating, setCreating] = useState(false);

    // Edit state (per widget)
    const [editTitles, setEditTitles] = useState<Record<string, string>>({});
    const [editModelIds, setEditModelIds] = useState<Record<string, string>>({});
    const [editModels, setEditModels] = useState<Record<string, TPromotedModel[]>>({});
    const [loadingModels, setLoadingModels] = useState<Record<string, boolean>>({});

    // Dirty title tracking
    const [dirtyTitles, setDirtyTitles] = useState<Record<string, boolean>>({});
    const [savingId, setSavingId] = useState<string | null>(null);

    // Inline copy feedback
    const [copiedId, setCopiedId] = useState<string | null>(null);

    // Hover tooltip for truncated widget names
    const [hoveredWidgetId, setHoveredWidgetId] = useState<string | null>(null);
    const titleRefs = useRef<Record<string, HTMLDivElement | null>>({});

    // Change model confirmation state
    const [confirmChange, setConfirmChange] = useState<{ widgetId: string; newModelId: string; title: string } | null>(null);
    const [changingModel, setChangingModel] = useState(false);

    // Delete confirmation state
    const [confirmDelete, setConfirmDelete] = useState<{ widgetId: string; title: string } | null>(null);
    const [deleting, setDeleting] = useState(false);

    // Panel-level Embed code dialog (ADO #14214 US-2 AC 1) — one dialog for the whole
    // channel, replacing the earlier per-widget dialog. Hosted in the DS Dialog at the end.
    const [showEmbedCode, setShowEmbedCode] = useState(false);
    // Snapshot of unviewed widget titles taken AT OPEN, before the optimistic clear below —
    // the Site code tab must still name them on the very visit that clears the flags
    // (ADO #14217 US-4 AC 3), so it can't just read widgets[].viewed once that flips.
    const [changedWidgetTitles, setChangedWidgetTitles] = useState<string[]>([]);

    const [fetchError, setFetchError] = useState(false);

    const fetchWidgets = useCallback(async (isInitial = false) => {
        if (isInitial) setLoading(true);
        setFetchError(false);
        try {
            const res = await listWidgets(industry, channel);
            if (res?.done) {
                const list: TWidget[] = res.data ?? [];
                setWidgets(list);
                const titles: Record<string, string> = {};
                const modelIds: Record<string, string> = {};
                list.forEach((w) => { titles[w.id] = w.title; modelIds[w.id] = w.model_id; });
                setEditTitles(titles);
                setEditModelIds(modelIds);
                setDirtyTitles({});
            } else {
                setFetchError(true);
            }
        } catch {
            setFetchError(true);
        } finally {
            if (isInitial) setLoading(false);
        }
    }, [industry, channel]);

    useEffect(() => { fetchWidgets(true); }, [fetchWidgets]);

    const fetchModelsForWidget = async (id: string) => {
        setLoadingModels((prev) => ({ ...prev, [id]: true }));
        try {
            const res = await getPromotedModelsForWidget(industry, channel, id);
            if (res?.done) {
                setEditModels((prev) => ({ ...prev, [id]: res.data ?? [] }));
            }
        } finally {
            setLoadingModels((prev) => ({ ...prev, [id]: false }));
        }
    };

    const handleToggle = async (id: string) => {
        if (expandedId === id) {
            setExpandedId(null);
        } else {
            setExpandedId(id);
            if (!editModels[id]) {
                fetchModelsForWidget(id);
            }
        }
    };

    const handleSaveTitle = async (id: string) => {
        const newTitle = editTitles[id]?.trim();
        const widget = widgets.find((w) => w.id === id);
        if (!newTitle) {
            toast.warning('Title is required');
            return;
        }
        if (newTitle === widget?.title) {
            setDirtyTitles((prev) => ({ ...prev, [id]: false }));
            return;
        }
        setSavingId(id);
        try {
            const res = await updateWidget(id, { title: newTitle });
            if (res?.done) {
                setDirtyTitles((prev) => ({ ...prev, [id]: false }));
                await fetchWidgets(false);
                toast.success('Widget title updated');
            } else {
                toast.error(res?.message ?? 'Failed to update title');
                setEditTitles((prev) => ({ ...prev, [id]: widget?.title ?? '' }));
            }
        } finally {
            setSavingId(null);
        }
    };

    const handleCancelTitleEdit = (id: string, originalTitle: string) => {
        setEditTitles((prev) => ({ ...prev, [id]: originalTitle }));
        setDirtyTitles((prev) => ({ ...prev, [id]: false }));
    };

    const handleModelSelect = (id: string, newModelId: string, title: string) => {
        if (newModelId === editModelIds[id]) return;
        setConfirmChange({ widgetId: id, newModelId, title });
    };

    const handleConfirmModelChange = async () => {
        if (!confirmChange) return;
        const widgetId = confirmChange.widgetId;
        setChangingModel(true);
        try {
            const res = await updateWidget(widgetId, { model_id: confirmChange.newModelId });
            if (res?.done) {
                await fetchWidgets(false);
                setEditModels((prev) => { const next = { ...prev }; delete next[widgetId]; return next; });
                fetchModelsForWidget(widgetId);
                toast.success('Widget model updated successfully');
                toast.info('Updating product mappings...', { duration: 3000 });
                onProjectListRefresh?.();
            } else {
                toast.error('Failed to change model mapping. Please try again.');
            }
        } finally {
            setChangingModel(false);
            setConfirmChange(null);
        }
    };

    const handleDeleteWidget = async () => {
        if (!confirmDelete) return;
        const { widgetId, title } = confirmDelete;
        setDeleting(true);
        try {
            const res = await deleteWidget(widgetId);
            if (res?.done) {
                await fetchWidgets(false);
                if (expandedId === widgetId) setExpandedId(null);
                toast.success(`Widget '${title}' deleted successfully`);
                onProjectListRefresh?.();
            } else {
                toast.error(res?.message ?? 'Failed to delete widget. Please try again.');
            }
        } finally {
            setDeleting(false);
            setConfirmDelete(null);
        }
    };

    const openCreateModal = async () => {
        setCreateTitle('');
        setCreateModelId('');
        const res = await getPromotedModelsForWidget(industry, channel);
        setCreateModels(res?.done ? (res.data ?? []) : []);
        setShowCreateModal(true);
    };

    const handleCreate = async () => {
        if (!createTitle.trim()) {
            toast.warning('Title is required');
            return;
        }
        if (!createModelId) {
            toast.warning('Model is required');
            return;
        }
        setCreating(true);
        try {
            const title = createTitle.trim();
            const res = await createWidget({ title, model_id: createModelId, channel, industry });
            if (res?.done) {
                const newId: string | undefined = res.data?.id;
                await fetchWidgets(false);
                setShowCreateModal(false);
                toast.success(`Widget '${title}' created successfully`);
                toast.info('Updating product mappings...', { duration: 3000 });
                onProjectListRefresh?.();
                if (newId) {
                    setExpandedId(newId);
                    await fetchModelsForWidget(newId);
                }
            } else {
                toast.error(res?.message ?? 'Failed to create widget');
            }
        } finally {
            setCreating(false);
        }
    };

    const copyToClipboard = (id: string) => {
        navigator.clipboard.writeText(id).then(() => {
            setCopiedId(id);
            setTimeout(() => setCopiedId(null), 2000);
        });
    };

    // Opening the Embed code dialog clears the New tags and the update dot — the
    // merchant is deemed to have seen the change (ADO #14217 US-4 AC 2; copying no
    // longer clears, reversing the 10-Sept decision). The Site code tab must still
    // name what changed on this very visit (AC 3), so the unviewed titles are
    // snapshotted BEFORE the optimistic clear below.
    const openEmbedCode = () => {
        const unviewedTitles = widgets.filter((w) => !w.viewed).map((w) => w.title);
        setChangedWidgetTitles(unviewedTitles);
        setShowEmbedCode(true);
        if (unviewedTitles.length > 0) {
            setWidgets((prev) => prev.map((w) => ({ ...w, viewed: true })));
            markWidgetsViewed(industry, channel);
        }
    };

    const renderWidgetTitle = (widget: TWidget, isExpanded: boolean) => (
        <span
            className="tw-min-w-0 tw-relative tw-block"
            onMouseEnter={() => {
                const el = titleRefs.current[widget.id];
                if (el && el.scrollWidth > el.offsetWidth) {
                    setHoveredWidgetId(widget.id);
                }
            }}
            onMouseLeave={() => setHoveredWidgetId(null)}
        >
            <span className="tw-flex tw-items-center tw-gap-1.5 tw-min-w-0">
                <span
                    ref={(el) => { titleRefs.current[widget.id] = el as HTMLDivElement | null; }}
                    className="tw-truncate tw-text-title-header tw-text-ink tw-min-w-0"
                >{editTitles[widget.id] ?? widget.title}</span>
                {!widget.viewed && (
                    <Tag label="New" tone="warning" data-testid={`guide-widget-new-tag-${widget.id}`} />
                )}
            </span>
            {!isExpanded && (
                <span className="tw-block tw-text-sub-body-supporting tw-text-ink-secondary tw-mt-0.5 tw-truncate tw-font-normal">
                    {widget.model_name ? `Model: ${widget.model_name}` : 'Model: —'}
                </span>
            )}
            {hoveredWidgetId === widget.id && (
                <span className="tw-absolute tw-top-full tw-left-0 tw-mt-1 tw-z-[100] tw-pointer-events-none tw-block">
                    <span className="tw-block tw-bg-surface-sunken tw-text-ink tw-text-sub-body-supporting tw-font-normal tw-px-2 tw-py-1.5 tw-rounded-ctl tw-shadow-ds-lg tw-border tw-border-edge tw-max-w-[360px] tw-break-words tw-whitespace-normal">
                        {(editTitles[widget.id] ?? widget.title).slice(0, 150)}
                    </span>
                </span>
            )}
        </span>
    );

    return (
        <>
            <SidePanel
                isOpen
                onClose={onClose}
                title="Widgets"
                tabs={[]}
                width="md"
                data-testid="guide-widget-panel"
                headerActions={
                    <>
                        {widgets.length > 0 && (
                            <span className="tw-relative tw-inline-flex">
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    id="open-widget-embed-code-btn"
                                    data-testid="widgets-setup-code-btn"
                                    onClick={openEmbedCode}
                                >
                                    Embed code
                                </Button>
                                {widgets.some((w) => !w.viewed) && (
                                    <span
                                        aria-hidden="true"
                                        data-testid="widgets-setup-dot"
                                        className="tw-absolute -tw-top-0.5 -tw-right-0.5 tw-h-2 tw-w-2 tw-rounded-full tw-bg-tone-warning-bold tw-ring-2 tw-ring-surface"
                                    />
                                )}
                                <span className="tw-sr-only">
                                    {widgets.some((w) => !w.viewed) ? 'Embed code has changed' : ''}
                                </span>
                            </span>
                        )}
                        {canCreateWidgets && (
                            <Button variant="primary" size="sm" onClick={openCreateModal} data-testid="components-guidewidgetpanel-button-1">
                                Create
                            </Button>
                        )}
                        <Button
                            variant="subtle"
                            size="sm"
                            aria-label="Close widget panel"
                            title="Close widget panel"
                            data-testid="components-guidewidgetpanel-button-2"
                            onClick={onClose}
                        >
                            <XIcon />
                        </Button>
                    </>
                }
            >
                {fetchError && widgets.length > 0 && (
                    <Alert
                        tone="warning"
                        className="tw-mb-3"
                        data-testid="guide-widget-refresh-alert"
                        actions={[{ label: 'Retry', onClick: () => fetchWidgets(false), 'data-testid': 'components-guidewidgetpanel-button-3' }]}
                    >
                        Could not refresh widgets.
                    </Alert>
                )}
                {loading ? (
                    <div className="tw-flex tw-justify-center tw-py-10">
                        <Spinner size="md" label="Loading..." showLabel data-testid="guide-widget-loading" />
                    </div>
                ) : fetchError && widgets.length === 0 ? (
                    <ErrorState
                        title="Failed to load widgets"
                        description="Please try again."
                        action={
                            <Button variant="primary" onClick={() => fetchWidgets(true)} data-testid="components-guidewidgetpanel-button-4">
                                Retry
                            </Button>
                        }
                        data-testid="guide-widget-error"
                    />
                ) : !fetchError && widgets.length === 0 ? (
                    <EmptyState
                        title="No widgets created"
                        description="Create a widget to generate an API key for a model."
                        illustration={<NoDataIllustration />}
                        action={
                            <Button variant="primary" onClick={openCreateModal} data-testid="components-guidewidgetpanel-button-5">
                                Create Widget
                            </Button>
                        }
                        data-testid="guide-widget-empty"
                    />
                ) : (
                    <div className="tw-space-y-2">
                        {widgets.map((widget) => {
                            const isExpanded = expandedId === widget.id;
                            const isTitleDirty = dirtyTitles[widget.id] ?? false;
                            const isSavingThis = savingId === widget.id;
                            const isLoadingModels = loadingModels[widget.id] ?? false;
                            const strippedId = widget.id.replace(/-/g, '');
                            const currentOpt = { value: widget.model_id, label: widget.model_name ?? widget.model_id };
                            const otherOpts = (editModels[widget.id] ?? [])
                                .filter((m) => m.id !== widget.model_id)
                                .map((m) => ({ value: m.id, label: m.name }));
                            const allOpts = [currentOpt, ...otherOpts];
                            return (
                                <Accordion
                                    key={widget.id}
                                    title={renderWidgetTitle(widget, isExpanded)}
                                    open={isExpanded}
                                    onOpenChange={() => handleToggle(widget.id)}
                                    data-testid={`guide-widget-toggle-${widget.id}`}
                                    className="tw-border tw-border-edge tw-rounded-ctl tw-px-4"
                                >
                                    <div className="tw-pb-4 tw-space-y-3">
                                        {/* Title field */}
                                        <TextField
                                            label="Title"
                                            value={editTitles[widget.id] ?? ''}
                                            onChange={(value) => {
                                                setEditTitles((prev) => ({ ...prev, [widget.id]: value }));
                                                setDirtyTitles((prev) => ({ ...prev, [widget.id]: true }));
                                            }}
                                            maxLength={100}
                                            id={`guide-widget-title-input-${widget.id}`}
                                            data-testid={`guide-widget-title-input-${widget.id}`}
                                        />

                                        {/* Model dropdown — searchable DS Dropdown replacing ReactSelect.
                                            Disabled when there is nothing to change to (single option)
                                            or the user lacks update permission, as before.
                                            `usePortal` (2026-09-08): the panel body is an overflow-y-auto
                                            scroller, so the inline listbox clipped at its bottom edge —
                                            the same defect the create dialog's dropdown showed (see it
                                            below). The portal escapes the scroller; SidePanel dismissal
                                            is untouched (useDismissable's isInside already whitelists
                                            `[data-dropdown-portal]` clicks via Dialog's shared marker). */}
                                        <Dropdown
                                            label="Model"
                                            searchable
                                            usePortal
                                            options={allOpts}
                                            value={editModelIds[widget.id] ?? widget.model_id}
                                            onChange={(value) => handleModelSelect(widget.id, String(value), editTitles[widget.id] ?? widget.title)}
                                            disabled={!canUpdateWidgets || (!isLoadingModels && allOpts.length <= 1)}
                                            data-testid={`guide-widget-model-select-${widget.id}`}
                                        />

                                        {/* Key field */}
                                        <div>
                                            <div className="tw-flex tw-items-center tw-gap-1 tw-mb-1">
                                                <Typography variant="sub-title-label" as="span">Key</Typography>
                                                <InfoTooltip
                                                    message="Use this key in your host application to reference this widget"
                                                    label="About Key"
                                                    usePortal
                                                />
                                            </div>
                                            <div className="tw-flex tw-items-center tw-gap-2">
                                                <TextField
                                                    label=""
                                                    aria-label="Key"
                                                    readOnly
                                                    value={strippedId}
                                                    onChange={() => { /* read-only */ }}
                                                    className="tw-flex-1"
                                                    id={`guide-widget-key-input-${widget.id}`}
                                                    data-testid={`guide-widget-key-input-${widget.id}`}
                                                />
                                                {/* Design-parity (ADO #14203 mockup): a single secondary
                                                    icon+label button whose label swaps to "Copied" in place —
                                                    same pattern EmbedCodeDialog's CodeBlock Copy buttons use —
                                                    not an icon-only button that gets replaced by separate text. */}
                                                <Button
                                                    variant="secondary"
                                                    size="sm"
                                                    icon={<CopyIconBlue />}
                                                    iconPosition="start"
                                                    onClick={() => copyToClipboard(strippedId)}
                                                    data-testid={`guide-widget-key-copy-${widget.id}`}
                                                >
                                                    {copiedId === strippedId ? 'Copied' : 'Copy'}
                                                </Button>
                                            </div>
                                        </div>

                                        {/* Delete — visible to users with delete permission. Embed code moved to the panel
                                            header (ADO #14214 US-2 AC 1): it now covers every widget in one dialog, not
                                            just this one. */}
                                        {canDeleteWidgets && (
                                            <div className="tw-flex tw-items-center">
                                                <Button
                                                    variant="danger"
                                                    size="sm"
                                                    icon={<Delete size={16} aria-hidden="true" />}
                                                    iconPosition="start"
                                                    onClick={() => setConfirmDelete({ widgetId: widget.id, title: editTitles[widget.id] ?? widget.title })}
                                                    data-testid={`guide-widget-delete-btn-${widget.id}`}
                                                >
                                                    Delete
                                                </Button>
                                            </div>
                                        )}

                                        {/* Save / Cancel — only when the title is dirty and the user may update */}
                                        {isTitleDirty && canUpdateWidgets && (
                                            <div className="tw-flex tw-gap-2 tw-pt-1">
                                                <Button
                                                    variant="primary"
                                                    size="sm"
                                                    onClick={() => handleSaveTitle(widget.id)}
                                                    disabled={isSavingThis}
                                                    data-testid={`guide-widget-save-btn-${widget.id}`}
                                                >
                                                    {isSavingThis ? 'Saving…' : 'Save'}
                                                </Button>
                                                <Button
                                                    variant="secondary"
                                                    size="sm"
                                                    onClick={() => { handleCancelTitleEdit(widget.id, widget.title); setExpandedId(null); }}
                                                    disabled={isSavingThis}
                                                    data-testid={`guide-widget-cancel-btn-${widget.id}`}
                                                >
                                                    Cancel
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                </Accordion>
                            );
                        })}
                    </div>
                )}
            </SidePanel>

            {/* Create widget dialog. Copy/layout matches the design mockup (Design Artefact
                linked on ADO #14203): title-field placeholder + helper text, model-field
                helper text, the "changes your Embed code" callout (US-1 AC 4 — deferred
                until Widget placements existed as a real tab, which it now does), and
                "Create widget" as the submit label rather than a bare "Create". */}
            {showCreateModal && (
                <Dialog
                    isOpen
                    onClose={() => setShowCreateModal(false)}
                    title="Create widget"
                    size={640}
                    data-testid="guide-widget-create-dialog"
                    footer={
                        <div className="tw-flex tw-w-full tw-gap-2 tw-justify-end">
                            <Button variant="secondary" onClick={() => setShowCreateModal(false)} data-testid="components-guidewidgetpanel-button-14">
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                onClick={handleCreate}
                                disabled={creating || !createTitle.trim() || !createModelId}
                                data-testid="components-guidewidgetpanel-button-15"
                            >
                                {creating ? 'Creating…' : 'Create widget'}
                            </Button>
                        </div>
                    }
                >
                    <div className="tw-space-y-4">
                        <TextField
                            label="Title"
                            placeholder="e.g. LS Fixtures PDP Model"
                            helperText="Shown to shoppers as the builder’s heading."
                            value={createTitle}
                            onChange={(value) => setCreateTitle(value)}
                            maxLength={100}
                            autoFocus
                            onKeyDown={(e) => { if (e.key === 'Enter') handleCreate(); }}
                            id="guide-widget-create-title"
                            data-testid="components-guidewidgetpanel-input-13"
                        />
                        {createModels.length === 0 ? (
                            <div>
                                <Typography variant="sub-title-label" as="div" className="tw-mb-1.5">Model</Typography>
                                <Typography variant="sub-body-supporting" as="p" className="tw-text-ink-secondary">
                                    No available models. Promote a model first.
                                </Typography>
                            </div>
                        ) : (
                            /* `usePortal` (2026-09-08): rendered inline, the listbox sat INSIDE the
                               Dialog's overflow-y-auto content and clipped at the footer band — the
                               search field showed and the options were cut off (user screenshot).
                               The portal renders it fixed over the dialog; Dialog's outside-click
                               guard already treats `[data-dropdown-portal]` as inside, so picking a
                               model cannot dismiss the dialog. */
                            <Dropdown
                                label="Model"
                                searchable
                                usePortal
                                placeholder="Select a promoted model"
                                helperText="Only promoted models can be attached. Repointing a widget at another model later does not change the code."
                                options={createModels.map((m) => ({ value: m.id, label: m.name }))}
                                value={createModelId || undefined}
                                onChange={(value) => setCreateModelId(String(value ?? ''))}
                                data-testid="guide-widget-create-model-select"
                            />
                        )}
                        <Alert tone="info" data-testid="guide-widget-create-embed-note">
                            This adds the widget to your <strong>Embed code</strong>. Copy the site code again and
                            replace the block on your site — how the widget is placed is chosen there, under Widget
                            placements.
                        </Alert>
                    </div>
                </Dialog>
            )}

            {/* Change Model confirmation */}
            {confirmChange && (
                <Dialog
                    isOpen
                    onClose={() => setConfirmChange(null)}
                    title={`Change model for ${confirmChange.title}`}
                    data-testid="guide-widget-change-model-dialog"
                    footer={
                        <div className="tw-flex tw-w-full tw-gap-2 tw-justify-end">
                            <Button variant="secondary" onClick={() => setConfirmChange(null)} data-testid="components-guidewidgetpanel-button-16">
                                Cancel
                            </Button>
                            <Button variant="primary" onClick={handleConfirmModelChange} disabled={changingModel} data-testid="components-guidewidgetpanel-button-17">
                                {changingModel ? 'Updating...' : 'Change Model'}
                            </Button>
                        </div>
                    }
                >
                    <Typography variant="body-primary" as="p" className="tw-text-ink-secondary">
                        This will update which products use this model. The change will take effect immediately.
                    </Typography>
                </Dialog>
            )}

            {/* Delete confirmation — DS Dialog tone=danger + primary confirm (§ 84 pattern). */}
            {/* Delete confirm — title/body structure kept consistent with the shared ISR/Guide
                destructive-confirm pattern (DeleteProjectModal.tsx, § 84/§ 92: title-in-header +
                one body paragraph, used by the UI-Model table, AB testing and Input Parameters
                too), not the ADO #14203 mockup's separate title/lead-line split, which no other
                delete confirm in this module uses. Content brought in line with the mockup where
                it was genuinely missing: the Embed code re-copy consequence, and "Delete widget"
                as the confirm label instead of a bare "Delete". */}
            {confirmDelete && (
                <Dialog
                    isOpen
                    onClose={() => setConfirmDelete(null)}
                    title={`Delete "${confirmDelete.title}"?`}
                    tone="danger"
                    data-testid="guide-widget-delete-dialog"
                    footer={
                        <div className="tw-flex tw-w-full tw-gap-2 tw-justify-end">
                            <Button variant="secondary" onClick={() => setConfirmDelete(null)} data-testid="components-guidewidgetpanel-button-18">
                                Cancel
                            </Button>
                            <Button variant="danger-primary" onClick={handleDeleteWidget} disabled={deleting} data-testid="components-guidewidgetpanel-button-19">
                                {deleting ? 'Deleting…' : 'Delete widget'}
                            </Button>
                        </div>
                    }
                >
                    <Typography variant="body-primary" as="p" className="tw-text-ink-secondary">
                        This permanently deletes the widget’s key. Any page using that key stops working, and
                        the Embed code will need copying again. This cannot be undone.
                    </Typography>
                </Dialog>
            )}

            {/* Embed code — panel-level dialog (ADO #14214 US-2 AC 1): one dialog for the whole
                channel, covering every widget across its three tabs, replacing the earlier
                per-widget popup. It portals to body on the overlay layer and mounts after the
                SidePanel, so it opens above the panel. The Dialog owns width, scrolling and the
                Done action (footer); the body is the tabs only (2026-09-22). */}
            {showEmbedCode && (
                <Dialog
                    isOpen
                    onClose={() => setShowEmbedCode(false)}
                    title="Embed code"
                    size={720}
                    data-testid="embed-code-popup"
                    footer={
                        <div className="tw-flex tw-w-full tw-justify-end">
                            <Button
                                variant="primary"
                                id="embed-code-dialog-close-btn"
                                data-testid="embed-code-dialog-close-btn"
                                onClick={() => setShowEmbedCode(false)}
                            >
                                Done
                            </Button>
                        </div>
                    }
                >
                    <EmbedCodeDialog
                        merchantKey={channel}
                        industry={industry}
                        widgets={widgets.map((w) => ({
                            id: w.id.replace(/-/g, ''),
                            title: w.title,
                            modelId: w.model_id,
                            modelName: w.model_name ?? w.model_id,
                        }))}
                        newWidgetTitles={changedWidgetTitles}
                    />
                </Dialog>
            )}
        </>
    );
};

export default GuideWidgetPanel;
